/**
 * KAN-650: смена прокси у подключённого Telegram-аккаунта (без повторного входа).
 * Логика формы вне компонента: проверка полей, тело запроса, тексты.
 * Свежесть сессии: `@/lib/telegramSessionAge` (общая с проверкой здоровья, KAN-661).
 * KAN-655: разбор строки прокси от провайдера в поля формы и сборка URL для API.
 */
import { isPersonalTelegramAccount } from '@/lib/telegramPersonalAccount';
import type { ProxyChangeBody, ProxyType, TelegramAccount } from '@/types/telegramAccount';

export const PROXY_TYPES: ReadonlyArray<{ value: ProxyType; label: string }> = [
  { value: 'socks5', label: 'SOCKS5' },
  { value: 'socks4', label: 'SOCKS4' },
  { value: 'http', label: 'HTTP' },
];

/** Текст сервера при 409 (тот же показывается заранее для свежей сессии). */
export const FRESH_PROXY_WARNING =
  'Сессия моложе суток: смена IP может привести к сбросу сессии Telegram';
export const PROXY_SAVE_ERROR = 'Не удалось сменить прокси';

const HOST_RE = /^[A-Za-z0-9.-]{1,253}$/;

export interface ProxyFormValues {
  type: ProxyType;
  host: string;
  port: string;
  username: string;
  password: string;
}

/** KAN-715: тип по умолчанию HTTP (прокси владельца HTTP); SOCKS5 выбирается вручную. */
export const EMPTY_PROXY_FORM: ProxyFormValues = {
  type: 'http',
  host: '',
  port: '',
  username: '',
  password: '',
};

/** Можно ли менять прокси из кабинета: не основной и не списанный аккаунт. */
export function canChangeProxy(account: TelegramAccount): boolean {
  // KAN-871: у личного аккаунта менеджера прокси не меняется (backend 409).
  return account.account_id !== 'primary' && !account.decommissioned && !isPersonalTelegramAccount(account);
}

/** Ошибка формы или null; сервер проверяет то же самое. */
export function validateProxyForm(v: ProxyFormValues): string | null {
  const host = v.host.trim();
  if (!host) return 'Укажите адрес прокси';
  if (!HOST_RE.test(host)) return 'Адрес прокси: домен или IPv4 без пробелов';
  const port = Number(v.port);
  if (!Number.isInteger(port) || port < 1 || port > 65535) {
    return 'Порт прокси: число от 1 до 65535';
  }
  if (v.password && !v.username.trim()) return 'Пароль прокси указан без логина';
  return null;
}

/** Тело запроса: поля прокси (пустые логин и пароль не передаются) и подтверждение. */
export function proxyBody(v: ProxyFormValues, confirm: boolean): ProxyChangeBody {
  const body: ProxyChangeBody = { type: v.type, host: v.host.trim(), port: Number(v.port) };
  if (v.username.trim()) body.username = v.username.trim();
  if (v.password) body.password = v.password;
  if (confirm) body.confirm = true;
  return body;
}

/** KAN-713: тело запроса с прокси из справочника. */
export function directoryProxyBody(proxyId: number, confirm: boolean): ProxyChangeBody {
  return confirm ? { proxy_id: proxyId, confirm: true } : { proxy_id: proxyId };
}

/** Тело запроса "Убрать прокси". */
export function removeBody(confirm: boolean): ProxyChangeBody {
  return confirm ? { remove: true, confirm: true } : { remove: true };
}

/** Начальные значения формы из текущего host:port строки таблицы (логин и пароль API не отдаёт). */
export function formFromHost(proxyHost: string | undefined): ProxyFormValues {
  const match = /^(.+):(\d{1,5})$/.exec((proxyHost ?? '').trim());
  return match ? { ...EMPTY_PROXY_FORM, host: match[1], port: match[2] } : { ...EMPTY_PROXY_FORM };
}

/** Все поля пустые: прокси не задан (в форме подключения он необязателен). */
export function isProxyFormEmpty(v: ProxyFormValues): boolean {
  return !v.host.trim() && !v.port.trim() && !v.username.trim() && !v.password;
}

/** Проверка необязательного прокси: пустая форма проходит, заполненная проверяется целиком. */
export function validateOptionalProxy(v: ProxyFormValues): string | null {
  return isProxyFormEmpty(v) ? null : validateProxyForm(v);
}

// ------------------------------------------------- разбор строки провайдера

/** Подсказка под полем "Адрес". */
export const PROXY_PASTE_HINT =
  'Можно вставить строку целиком: socks5://логин:пароль@адрес:порт или адрес:порт:логин:пароль';

/** Схема URL или слово в строке таблицы -> тип прокси, который принимает backend. */
const SCHEME_TYPES: Record<string, ProxyType> = {
  socks5: 'socks5',
  socks5h: 'socks5',
  socks4: 'socks4',
  socks4a: 'socks4',
  http: 'http',
  https: 'http',
};

const PORT_RE = /^\d{1,5}$/;

function validPort(port: string): boolean {
  if (!PORT_RE.test(port)) return false;
  const n = Number(port);
  return n >= 1 && n <= 65535;
}

/** `host:port` -> поля или null (хост: домен или IPv4, порт 1..65535). */
function parseHostPort(text: string): Pick<ProxyFormValues, 'host' | 'port'> | null {
  const match = /^([^:/]+):(\d{1,5})$/.exec(text);
  if (!match || !HOST_RE.test(match[1]) || !validPort(match[2])) return null;
  return { host: match[1], port: match[2] };
}

/** Логин и пароль из URL могут быть percent-encoded; битую кодировку оставляем как есть. */
function decodePart(text: string): string {
  try {
    return decodeURIComponent(text);
  } catch {
    return text;
  }
}

/** `[user[:pass]@]host:port[/...]`; decode: часть URL, логин и пароль декодируются. */
function parseAuthority(text: string, decode: boolean): Partial<ProxyFormValues> | null {
  const at = text.lastIndexOf('@');
  const hostPart = at >= 0 ? text.slice(at + 1) : text;
  const hostPort = parseHostPort(hostPart.replace(/\/.*$/, ''));
  if (!hostPort) return null;
  if (at < 0) return hostPort;
  const userInfo = text.slice(0, at);
  const colon = userInfo.indexOf(':');
  const username = colon >= 0 ? userInfo.slice(0, colon) : userInfo;
  const password = colon >= 0 ? userInfo.slice(colon + 1) : '';
  if (!username) return null;
  return {
    ...hostPort,
    username: decode ? decodePart(username) : username,
    password: decode ? decodePart(password) : password,
  };
}

/** Одно слово без пробелов: URL, `user:pass@host:port`, `host:port:user:pass`, `host:port`. */
function parseToken(token: string): Partial<ProxyFormValues> | null {
  const url = /^([a-z0-9]+):\/\/(.+)$/i.exec(token);
  if (url) {
    const type = SCHEME_TYPES[url[1].toLowerCase()];
    if (!type) return null;
    const fields = parseAuthority(url[2], true);
    return fields && { type, ...fields };
  }
  if (token.includes('@')) return parseAuthority(token, true);
  const parts = token.split(':');
  if (parts.length < 2) return null;
  const hostPort = parseHostPort(`${parts[0]}:${parts[1]}`);
  if (!hostPort) return null;
  if (parts.length === 2 || !parts[2]) return hostPort;
  return { ...hostPort, username: parts[2], password: parts.slice(3).join(':') };
}

/**
 * Строка прокси от провайдера -> найденные поля формы или null, если это не похоже
 * на прокси-строку (голый хост, мусор). Форматы: `socks5://user:pass@host:port`
 * (схемы socks5, socks5h, socks4, socks4a, http, https), `user:pass@host:port`,
 * `host:port:user:pass`, строка таблицы `host:port user pass [HTTP]`, `host:port`.
 * `type` возвращается только если выведен из схемы или слова HTTP/SOCKS5/SOCKS4.
 */
export function parseProxyString(raw: string): Partial<ProxyFormValues> | null {
  const tokens = (raw ?? '').trim().split(/\s+/).filter(Boolean);
  if (!tokens.length) return null;
  if (tokens.length === 1) return parseToken(tokens[0]);
  // строка таблицы: host:port, затем логин, пароль и необязательное слово типа в конце
  const rest = tokens.slice(1);
  const type: ProxyType | undefined = SCHEME_TYPES[rest[rest.length - 1].toLowerCase()];
  if (type) rest.pop();
  if (rest.length > 2) return null;
  const first = parseToken(tokens[0]);
  if (!first) return null;
  const fields: Partial<ProxyFormValues> = { ...first };
  if (rest[0]) fields.username = rest[0];
  if (rest[1]) fields.password = rest[1];
  if (type) fields.type = type;
  return fields;
}

/** Поля формы -> `type://user:pass@host:port` для API; без логина `type://host:port`, без хоста ''. */
export function proxyToUrl(v: ProxyFormValues): string {
  const host = v.host.trim();
  if (!host) return '';
  const username = v.username.trim();
  const auth = username
    ? `${encodeURIComponent(username)}${v.password ? `:${encodeURIComponent(v.password)}` : ''}@`
    : '';
  const port = v.port.trim();
  return `${v.type}://${auth}${host}${port ? `:${port}` : ''}`;
}

/** Код ошибки ответа API (`code`), если есть. */
export function errorCode(err: unknown): string {
  const code = (err as { response?: { data?: { code?: unknown } } })?.response?.data?.code;
  return typeof code === 'string' ? code : '';
}

/** Текст ответа после сохранения. */
export function savedToast(status: string, removed: boolean): string {
  const what = removed ? 'Прокси убран' : 'Прокси сохранён';
  if (status === 'connected') return `${what}: аккаунт переподключён`;
  if (status === 'pending') return `${what}: аккаунт переподключится в течение минуты`;
  return `${what}: подключится при включении аккаунта`;
}
